import type { SceneObject } from '../../exports/scene';
import type { AutoUIData } from '../../exports/behaviors';

export interface InspectorOptions {
    title?: string;
    readout?: string;
    width?: number;
    views?: { label: string; yaw: number; pitch: number; radiusScale?: number }[];
}
const DEFAULT_VIEWS: NonNullable<InspectorOptions['views']> = [
    { label: 'Overview', yaw: 135, pitch: 65 },
    { label: 'Front', yaw: 90, pitch: 90 },
    { label: 'Top', yaw: 90, pitch: 5 },
];

/** A generic authoring recipe: ordinary native AutoUI data, no runtime helper. */
export function inspectorData(owner: SceneObject, focus: SceneObject, options: InspectorOptions = {}): AutoUIData {
    if (!owner.getBehavior('Properties')) throw new Error('Inspector owner requires Properties first');
    if (owner.traceScene !== focus.traceScene) throw new Error('Inspector focus must be in the same scene');
    const width = options.width ?? 300;
    if (!Number.isFinite(width) || width < 240 || width > 480) throw new Error('Inspector width must be between 240 and 480 CSS pixels');
    for (const field of ['title', 'readout'] as const) {
        if (options[field] !== undefined && typeof options[field] !== 'string') throw new Error(`${field} must be a string`);
    }
    const views = options.views ?? DEFAULT_VIEWS;
    if (!Array.isArray(views) || views.length < 1 || views.length > 4) throw new Error('Inspector requires 1–4 camera views');
    const target = owner.traceScene.getRelativePath(focus, owner);
    const cameraViews = views.map(view => {
        if (!view || typeof view.label !== 'string' || !view.label.trim() || view.label.length > 24) throw new Error('View labels must contain 1–24 characters');
        if (!Number.isFinite(view.yaw) || !Number.isFinite(view.pitch) || view.pitch < 0 || view.pitch > 180) throw new Error('View angles must be finite; pitch must be between 0 and 180 degrees');
        const radiusScale = view.radiusScale ?? 1.1;
        if (!Number.isFinite(radiusScale) || radiusScale < 1 || radiusScale > 3) throw new Error('View radiusScale must be between 1 and 3');
        return { label: view.label, yaw: view.yaw, pitch: view.pitch, radiusScale, target };
    });
    return {
        '@type': 'AutoUI', renderMode: 'ScreenSpaceOverlay', propertyGroups: ['$self'],
        title: options.title ?? 'Explore', width, position: { x: 12, y: 12 },
        collapsible: true, collapseBelowWidth: 600, collapseOnView: true,
        readout: options.readout ?? '', cameraViews,
    };
}
